// src/components/CookMode.jsx  —  ohapotato v2026.10.36
//
// 🍳 COOK MODE — full-screen centered dialog (like a confirmation box)
// Pops up above everything, works great on mobile + desktop.
// Backdrop click or Escape to close. Wake lock keeps screen on while cooking.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

function useWakeLock() {
  const ref = useRef(null);
  useEffect(() => {
    if (!('wakeLock' in navigator)) return;
    navigator.wakeLock.request('screen').then(l => { ref.current = l; }).catch(() => {});
    return () => { ref.current?.release().catch(() => {}); };
  }, []);
}

function parseSecs(str) {
  if (!str) return 0;
  const nums = str.match(/\d+/g);
  if (!nums) return 0;
  return parseInt(nums[nums.length - 1], 10) * 60;
}

function fmt(s) {
  if (s == null || s < 0) return '--:--';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function ping() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(), gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.22, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
    osc.start(); osc.stop(ctx.currentTime + 0.7);
  } catch (_) {}
}

function Ring({ secs, total, size = 96 }) {
  const R = 38, circ = 2 * Math.PI * R;
  const pct  = total > 0 ? Math.max(0, secs ?? total) / total : 1;
  const warn = secs !== null && secs <= 15 && secs > 0;
  const done = secs === 0;
  return (
    <svg width={size} height={size} viewBox="0 0 84 84"
      style={{ display: 'block', transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx="42" cy="42" r={R} fill="none" stroke="var(--border-base)" strokeWidth="6" />
      <circle cx="42" cy="42" r={R} fill="none"
        stroke={done ? 'var(--accent-green)' : warn ? '#EF4444' : 'var(--accent-orange)'}
        strokeWidth="6" strokeLinecap="round"
        strokeDasharray={`${circ * pct} ${circ}`}
        style={{ transition: 'stroke-dasharray 1s linear, stroke 0.3s ease' }}
      />
    </svg>
  );
}

function StepDots({ steps, idx, accent, onJump }) {
  return (
    <div style={{ display:'flex', gap:'7px', justifyContent:'center', flexWrap:'wrap', padding:'4px 0' }}>
      {steps.map((_, i) => (
        <button key={i} aria-label={`Step ${i + 1}`} onClick={() => onJump(i)}
          style={{
            width:'9px', height:'9px', borderRadius:'50%', border:'none',
            padding:0, cursor:'pointer', flexShrink:0,
            transition:'background 0.2s ease, transform 0.15s ease',
            background: i < idx ? 'var(--border-strong)' : i === idx ? accent : 'var(--border-base)',
            transform: i === idx ? 'scale(1.55)' : 'scale(1)',
          }}
        />
      ))}
    </div>
  );
}

export default function CookMode({ recipe, onClose }) {
  const [idx, setIdx]           = useState(0);
  const [secs, setSecs]         = useState(null);
  const [running, setRunning]   = useState(false);
  const [finished, setFinished] = useState(false);
  const timerRef = useRef(null);
  const bodyRef  = useRef(null);

  useWakeLock();

  const steps   = recipe?.steps || [];
  const step    = steps[idx];
  const total   = parseSecs(step?.duration);
  const isFirst = idx === 0;
  const isLast  = idx === steps.length - 1;
  const progress = steps.length > 0 ? (finished ? 1 : idx / steps.length) : 0;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    setRunning(false); setSecs(null);
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [idx]);

  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setSecs(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); setRunning(false); ping(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.target.tagName === 'BUTTON') return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  const goNext = useCallback(() => {
    if (isLast) { clearInterval(timerRef.current); setFinished(true); }
    else setIdx(i => i + 1);
  }, [isLast]);
  const goPrev = useCallback(() => { if (!isFirst) setIdx(i => i - 1); }, [isFirst]);

  const startTimer  = () => { setSecs(total); setRunning(true); };
  const pauseTimer  = () => { clearInterval(timerRef.current); setRunning(false); };
  const resumeTimer = () => setRunning(true);
  const resetTimer  = () => { clearInterval(timerRef.current); setRunning(false); setSecs(null); };

  const isCook   = step?.phase === 'cook';
  const accent   = isCook ? 'var(--accent-green)' : 'var(--accent-orange)';
  const accentBg = isCook ? 'var(--accent-green-bg)' : 'var(--accent-orange-bg)';

  return ReactDOM.createPortal(
    <div style={s.backdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div style={s.dialog} onClick={e => e.stopPropagation()}>

        {/* Top bar */}
        <div style={s.topBar}>
          <div style={s.topLeft}>
            <span style={{ ...s.phasePill, color: accent, background: accentBg }}>
              {finished ? '🎉 Done' : isCook ? '🔥 Cooking' : '🔪 Prep'}
            </span>
            <span style={s.recipeTitle}>{recipe.emoji} {recipe.title}</span>
          </div>
          <button style={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Progress bar */}
        <div style={s.progressTrack}>
          <div style={{ ...s.progressFill, width:`${progress*100}%`, background: accent }} />
        </div>

        {/* Scrollable body */}
        <div ref={bodyRef} style={s.body}>
          {finished ? (
            <div style={s.finishWrap}>
              <span style={s.finishEmoji}>{recipe.emoji || '🍽'}</span>
              <h2 style={s.finishTitle}>You did it!</h2>
              <p style={s.finishSub}>{recipe.title} is ready to serve 🥔</p>
              <button style={s.finishBtn} onClick={onClose}>← Back to recipes</button>
            </div>
          ) : (
            <>
              <StepDots steps={steps} idx={idx} accent={accent} onJump={setIdx} />
              <p style={s.stepLabel}>Step <strong>{idx + 1}</strong> of {steps.length}</p>

              {total > 0 && (
                <div style={{ ...s.timerCard, borderColor: accent + '55' }}>
                  <div style={s.ringWrap}>
                    <Ring secs={secs} total={total} size={96} />
                    <div style={s.ringInner}>
                      <span style={{
                        ...s.ringTime,
                        color: secs === 0 ? 'var(--accent-green)'
                             : secs !== null && secs <= 15 ? '#EF4444'
                             : 'var(--text-primary)',
                      }}>
                        {secs === 0 ? '✓' : fmt(secs ?? total)}
                      </span>
                      <span style={s.ringSub}>{secs === 0 ? 'done!' : 'timer'}</span>
                    </div>
                  </div>
                  <div style={s.timerRight}>
                    <span style={s.timerDuration}>⏱ {step.duration}</span>
                    <div style={s.timerBtns}>
                      {!running && secs === null && (
                        <button style={{ ...s.tBtn, background: accent, color:'#fff' }} onClick={startTimer}>▶ Start</button>
                      )}
                      {running && (
                        <button style={{ ...s.tBtn, background:'var(--bg-pill)', color:'var(--text-primary)', border:'1.5px solid var(--border-base)' }} onClick={pauseTimer}>⏸ Pause</button>
                      )}
                      {!running && secs !== null && secs > 0 && (<>
                        <button style={{ ...s.tBtn, background: accent, color:'#fff' }} onClick={resumeTimer}>▶ Resume</button>
                        <button style={{ ...s.tBtn, background:'transparent', color:'var(--text-muted)', border:'1.5px solid var(--border-base)' }} onClick={resetTimer}>↺</button>
                      </>)}
                      {secs === 0 && (
                        <button style={{ ...s.tBtn, background:'var(--accent-green)', color:'#fff' }} onClick={resetTimer}>↺ Reset</button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <p style={s.instruction}>{step?.instruction}</p>
            </>
          )}
        </div>

        {/* Footer nav */}
        {!finished && (
          <div style={s.footer}>
            <button style={{ ...s.navBtn, ...s.navPrev, opacity: isFirst ? 0.35 : 1 }}
              onClick={goPrev} disabled={isFirst}>← Prev</button>
            <button style={{ ...s.navBtn, ...s.navNext, background: accent }}
              onClick={goNext}>{isLast ? '✓ Finish' : 'Next →'}</button>
          </div>
        )}
      </div>
    </div>
  , document.body);
}

const s = {
  backdrop: {
    position:'fixed', inset:0, zIndex:500,
    background:'rgba(0,0,0,0.65)', backdropFilter:'blur(6px)',
    display:'flex', alignItems:'center', justifyContent:'center',
    padding:'16px',
    animation:'fadeUp 0.15s ease',
  },
  dialog: {
    width:'100%', maxWidth:'520px', maxHeight:'90vh',
    background:'var(--bg-surface)',
    borderRadius:'20px',
    boxShadow:'0 32px 80px rgba(0,0,0,0.4)',
    display:'flex', flexDirection:'column', overflow:'hidden',
    animation:'dialogPop 0.25s cubic-bezier(0.34,1.56,0.64,1)',
  },
  topBar: {
    display:'flex', alignItems:'center', gap:'10px',
    padding:'14px 16px 12px', borderBottom:'1px solid var(--border-base)', flexShrink:0,
  },
  topLeft: { display:'flex', alignItems:'center', gap:'9px', flex:1, minWidth:0 },
  phasePill: {
    fontSize:'10px', fontWeight:'700', letterSpacing:'0.5px',
    padding:'3px 10px', borderRadius:'var(--radius-pill)',
    fontFamily:'var(--font-body)', whiteSpace:'nowrap', flexShrink:0,
  },
  recipeTitle: {
    fontFamily:'var(--font-display)', fontSize:'14px',
    color:'var(--text-primary)',
    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
  },
  closeBtn: {
    width:'30px', height:'30px', borderRadius:'50%',
    border:'none', background:'var(--bg-pill)', fontSize:'13px',
    color:'var(--text-muted)', cursor:'pointer', flexShrink:0,
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  progressTrack: { height:'3px', background:'var(--border-base)', flexShrink:0 },
  progressFill:  { height:'100%', borderRadius:'0 2px 2px 0', transition:'width 0.4s ease, background 0.3s ease' },
  body: {
    flex:1, overflowY:'auto', padding:'20px 20px 12px',
    display:'flex', flexDirection:'column', gap:'16px',
  },
  stepLabel: {
    textAlign:'center', fontFamily:'var(--font-body)',
    fontSize:'12px', color:'var(--text-muted)',
  },
  timerCard: {
    display:'flex', alignItems:'center', gap:'16px',
    background:'var(--bg-card)', border:'1.5px solid',
    borderRadius:'var(--radius-lg)', padding:'14px 16px', flexShrink:0,
  },
  ringWrap: { position:'relative', width:'96px', height:'96px', flexShrink:0 },
  ringInner: {
    position:'absolute', inset:0,
    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
  },
  ringTime: {
    fontFamily:'var(--font-body)', fontSize:'17px', fontWeight:'800',
    letterSpacing:'-0.5px', lineHeight:'1',
  },
  ringSub: {
    fontSize:'9px', color:'var(--text-muted)',
    letterSpacing:'0.5px', marginTop:'2px', fontFamily:'var(--font-body)',
  },
  timerRight: { display:'flex', flexDirection:'column', gap:'9px', flex:1 },
  timerDuration: { fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' },
  timerBtns: { display:'flex', gap:'7px', flexWrap:'wrap' },
  tBtn: {
    padding:'7px 16px', border:'none', borderRadius:'var(--radius-pill)',
    fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:'600', cursor:'pointer',
  },
  instruction: {
    fontFamily:'var(--font-body)', fontSize:'15px',
    lineHeight:'1.75', color:'var(--text-primary)', margin:0,
  },
  footer: {
    display:'flex', gap:'8px',
    padding:'12px 16px', borderTop:'1px solid var(--border-base)', flexShrink:0,
  },
  navBtn: {
    padding:'12px 0', border:'none', borderRadius:'var(--radius-md)',
    fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'700', cursor:'pointer',
  },
  navPrev: { background:'var(--bg-pill)', color:'var(--text-secondary)', padding:'12px 20px' },
  navNext: { color:'#fff', flex:1 },
  finishWrap: {
    flex:1, display:'flex', flexDirection:'column',
    alignItems:'center', justifyContent:'center',
    textAlign:'center', padding:'32px 20px', gap:'12px',
  },
  finishEmoji: { fontSize:'60px', lineHeight:'1' },
  finishTitle: {
    fontFamily:'var(--font-display)', fontSize:'30px',
    color:'var(--text-primary)', margin:0,
  },
  finishSub: {
    fontFamily:'var(--font-body)', fontSize:'14px',
    color:'var(--text-muted)', margin:0, lineHeight:'1.5',
  },
  finishBtn: {
    marginTop:'8px', padding:'12px 28px', border:'none',
    borderRadius:'var(--radius-md)', cursor:'pointer',
    background:'var(--accent-orange)', color:'#fff',
    fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'700',
  },
};
